import React, { useMemo, useState, useEffect } from "react";
import ChartsDisplay from "./ChartComponent";
import DynamicModal from "../../Modal/DynamicModal";
import "./ChartPanel.css";

const AGGREGATIONS = [
    { value: "NONE", label: "Bez agregacije - sve vrijednosti" },
    { value: "COUNT", label: "COUNT - broj objekata" },
    { value: "SUM", label: "SUM - zbir" },
    { value: "AVG", label: "AVG - prosjek" },
    { value: "MIN", label: "MIN - minimum" },
    { value: "MAX", label: "MAX - maksimum" },
];

const FILTER_OPERATORS = [
    { value: "=", label: "=" },
    { value: "!=", label: "≠" },
    { value: ">", label: ">" },
    { value: ">=", label: "≥" },
    { value: "<", label: "<" },
    { value: "<=", label: "≤" },
    { value: "contains", label: "sadrži" },
];

const SORT_OPTIONS = [
    { value: "none", label: "Bez sortiranja" },
    { value: "x-asc", label: "X rastuće" },
    { value: "x-desc", label: "X opadajuće" },
    { value: "y-asc", label: "Y rastuće" },
    { value: "y-desc", label: "Y opadajuće" },
];

const ChartSidebar = ({ isOpen, inputUrl, setIsChartOpen }) => {
    const [selectedX, setSelectedX] = useState("");
    const [selectedY, setSelectedY] = useState("");
    const [aggregation, setAggregation] = useState("COUNT");

    const [filterColumn, setFilterColumn] = useState("");
    const [filterOperator, setFilterOperator] = useState("=");
    const [filterValue, setFilterValue] = useState("");

    const [sortBy, setSortBy] = useState("none");
    const [topLimit, setTopLimit] = useState("");

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const resetState = () => {
        setSelectedX("");
        setSelectedY("");
        setAggregation("COUNT");
        setFilterColumn("");
        setFilterOperator("=");
        setFilterValue("");
        setSortBy("none");
        setTopLimit("");
    };

    const onRequestClose = () => {
        setIsChartOpen(false);
        resetState();
    };

    useEffect(() => {
        if (!inputUrl) {
            setData(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        fetch(inputUrl)
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Network response was not ok");
                }

                return response.json();
            })
            .then((fetchedData) => {
                setData(fetchedData);
                setLoading(false);
            })
            .catch((err) => {
                setError(err.message);
                setLoading(false);
            });
    }, [inputUrl]);

    const features = useMemo(() => {
        return data?.features || [];
    }, [data]);

    const propertyKeys = useMemo(() => {
        if (!features.length) return [];

        const keys = new Set();

        features.forEach((feature) => {
            Object.keys(feature.properties || {}).forEach((key) => keys.add(key));
        });

        return Array.from(keys);
    }, [features]);

    const numericPropertyKeys = useMemo(() => {
        return propertyKeys.filter((key) =>
            features.some((feature) => {
                const value = feature.properties?.[key];

                return (
                    value !== null &&
                    value !== undefined &&
                    value !== "" &&
                    !Number.isNaN(Number(value))
                );
            })
        );
    }, [features, propertyKeys]);

    const compareValue = (rawValue, operator, targetValue) => {
        const value = rawValue ?? "";
        const valueText = String(value).toLowerCase();
        const targetText = String(targetValue).toLowerCase();

        const valueNumber = Number(value);
        const targetNumber = Number(targetValue);
        const canCompareNumbers =
            !Number.isNaN(valueNumber) && !Number.isNaN(targetNumber);

        if (operator === "contains") {
            return valueText.includes(targetText);
        }

        if (canCompareNumbers) {
            if (operator === "=") return valueNumber === targetNumber;
            if (operator === "!=") return valueNumber !== targetNumber;
            if (operator === ">") return valueNumber > targetNumber;
            if (operator === ">=") return valueNumber >= targetNumber;
            if (operator === "<") return valueNumber < targetNumber;
            if (operator === "<=") return valueNumber <= targetNumber;
        }

        if (operator === "=") return valueText === targetText;
        if (operator === "!=") return valueText !== targetText;

        return true;
    };

    const filteredFeatures = useMemo(() => {
        if (!features.length) return [];

        if (!filterColumn || filterValue === "") {
            return features;
        }

        return features.filter((feature) =>
            compareValue(
                feature.properties?.[filterColumn],
                filterOperator,
                filterValue
            )
        );
    }, [features, filterColumn, filterOperator, filterValue]);

    const chartResult = useMemo(() => {
        if (!filteredFeatures.length || !selectedX) {
            return {
                xValues: [],
                yValues: [],
                rawRows: [],
            };
        }

        if (aggregation === "NONE") {
            if (!selectedY) {
                return {
                    xValues: [],
                    yValues: [],
                    rawRows: [],
                };
            }

            let rows = filteredFeatures
                .map((feature, index) => {
                    const xRaw = feature.properties?.[selectedX];
                    const yRaw = feature.properties?.[selectedY];
                    const yValue = Number(yRaw);

                    if (
                        xRaw === null ||
                        xRaw === undefined ||
                        xRaw === "" ||
                        yRaw === null ||
                        yRaw === undefined ||
                        yRaw === "" ||
                        Number.isNaN(yValue)
                    ) {
                        return null;
                    }

                    return {
                        x: String(xRaw),
                        y: yValue,
                        originalIndex: index + 1,
                    };
                })
                .filter(Boolean);

            if (sortBy === "x-asc") {
                rows = [...rows].sort((a, b) => String(a.x).localeCompare(String(b.x)));
            }

            if (sortBy === "x-desc") {
                rows = [...rows].sort((a, b) => String(b.x).localeCompare(String(a.x)));
            }

            if (sortBy === "y-asc") {
                rows = [...rows].sort((a, b) => a.y - b.y);
            }

            if (sortBy === "y-desc") {
                rows = [...rows].sort((a, b) => b.y - a.y);
            }

            const parsedLimit = Number(topLimit);

            if (parsedLimit > 0) {
                rows = rows.slice(0, parsedLimit);
            }

            return {
                xValues: rows.map((row, index) => `${row.x} #${index + 1}`),
                yValues: rows.map((row) => Number(row.y.toFixed(2))),
                rawRows: rows,
            };
        }

        const groups = new Map();

        filteredFeatures.forEach((feature) => {
            const xRaw = feature.properties?.[selectedX];

            if (xRaw === null || xRaw === undefined || xRaw === "") return;

            const xKey = String(xRaw);

            if (!groups.has(xKey)) {
                groups.set(xKey, []);
            }

            groups.get(xKey).push(feature);
        });

        let rows = Array.from(groups.entries()).map(([xKey, groupFeatures]) => {
            if (aggregation === "COUNT") {
                return {
                    x: xKey,
                    y: groupFeatures.length,
                };
            }

            const values = groupFeatures
                .map((feature) => Number(feature.properties?.[selectedY]))
                .filter((value) => !Number.isNaN(value));

            if (!values.length) {
                return {
                    x: xKey,
                    y: 0,
                };
            }

            if (aggregation === "SUM") {
                return {
                    x: xKey,
                    y: values.reduce((sum, value) => sum + value, 0),
                };
            }

            if (aggregation === "AVG") {
                return {
                    x: xKey,
                    y:
                        values.reduce((sum, value) => sum + value, 0) /
                        values.length,
                };
            }

            if (aggregation === "MIN") {
                return {
                    x: xKey,
                    y: Math.min(...values),
                };
            }

            if (aggregation === "MAX") {
                return {
                    x: xKey,
                    y: Math.max(...values),
                };
            }

            return {
                x: xKey,
                y: groupFeatures.length,
            };
        });

        if (sortBy === "x-asc") {
            rows = [...rows].sort((a, b) => String(a.x).localeCompare(String(b.x)));
        }

        if (sortBy === "x-desc") {
            rows = [...rows].sort((a, b) => String(b.x).localeCompare(String(a.x)));
        }

        if (sortBy === "y-asc") {
            rows = [...rows].sort((a, b) => a.y - b.y);
        }

        if (sortBy === "y-desc") {
            rows = [...rows].sort((a, b) => b.y - a.y);
        }

        const parsedLimit = Number(topLimit);

        if (parsedLimit > 0) {
            rows = rows.slice(0, parsedLimit);
        }

        return {
            xValues: rows.map((row) => row.x),
            yValues: rows.map((row) => Number(row.y.toFixed(2))),
            rawRows: rows,
        };
    }, [
        filteredFeatures,
        selectedX,
        selectedY,
        aggregation,
        sortBy,
        topLimit,
    ]);

    const shouldShowChart =
        selectedX &&
        (aggregation === "COUNT" || selectedY) &&
        chartResult.xValues.length > 0;

    const chartTitle = useMemo(() => {
        if (!selectedX) return "";

        if (aggregation === "NONE") {
            return `${selectedY} po ${selectedX} - pojedinačne vrijednosti`;
        }

        if (aggregation === "COUNT") {
            return `Broj objekata po ${selectedX}`;
        }

        return `${aggregation} ${selectedY} po ${selectedX}`;
    }, [aggregation, selectedX, selectedY]);

    const renderFeatureInfo = () => {
        if (!features.length) {
            return (
                <div className="chart-empty-state">
                    Nema podataka za prikaz dijagrama.
                </div>
            );
        }

        return (
            <>
                <div className="chart-config-card">
                    <div className="chart-config-grid">
                        <label className="full-row">
                            <span>X-osa / grupisanje:</span>

                            <select
                                value={selectedX}
                                onChange={(event) => setSelectedX(event.target.value)}
                                title={selectedX}
                            >
                                <option value="">Odaberite X kolonu</option>

                                {propertyKeys.map((key) => (
                                    <option key={key} value={key} title={key}>
                                        {key}
                                    </option>
                                ))}
                            </select>

                            {selectedX && (
                                <small className="selected-axis-name">{selectedX}</small>
                            )}
                        </label>

                        <label>
                            <span>Agregacija:</span>

                            <select
                                value={aggregation}
                                onChange={(event) => {
                                    const nextAggregation = event.target.value;

                                    setAggregation(nextAggregation);

                                    if (nextAggregation === "COUNT") {
                                        setSelectedY("");
                                    }
                                }}
                            >
                                {AGGREGATIONS.map((item) => (
                                    <option key={item.value} value={item.value}>
                                        {item.label}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label>
                            <span>Y-osa:</span>

                            <select
                                value={selectedY}
                                onChange={(event) => setSelectedY(event.target.value)}
                                title={selectedY}
                                disabled={aggregation === "COUNT"}
                            >
                                <option value="">
                                    {aggregation === "COUNT"
                                        ? "Nije potrebno za COUNT"
                                        : "Odaberite Y kolonu"}
                                </option>

                                {numericPropertyKeys.map((key) => (
                                    <option key={key} value={key} title={key}>
                                        {key}
                                    </option>
                                ))}
                            </select>

                            {selectedY && (
                                <small className="selected-axis-name">{selectedY}</small>
                            )}
                        </label>

                        <label>
                            <span>Filter kolona:</span>

                            <select
                                value={filterColumn}
                                onChange={(event) => setFilterColumn(event.target.value)}
                                title={filterColumn}
                            >
                                <option value="">Bez filtera</option>

                                {propertyKeys.map((key) => (
                                    <option key={key} value={key} title={key}>
                                        {key}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label>
                            <span>Operator:</span>

                            <select
                                value={filterOperator}
                                onChange={(event) => setFilterOperator(event.target.value)}
                                disabled={!filterColumn}
                            >
                                {FILTER_OPERATORS.map((item) => (
                                    <option key={item.value} value={item.value}>
                                        {item.label}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label className="full-row">
                            <span>Vrijednost filtera:</span>

                            <input
                                type="text"
                                value={filterValue}
                                onChange={(event) => setFilterValue(event.target.value)}
                                disabled={!filterColumn}
                                placeholder="Unesi vrijednost filtera"
                            />
                        </label>

                        <label>
                            <span>Sortiranje:</span>

                            <select
                                value={sortBy}
                                onChange={(event) => setSortBy(event.target.value)}
                            >
                                {SORT_OPTIONS.map((item) => (
                                    <option key={item.value} value={item.value}>
                                        {item.label}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label>
                            <span>Top N:</span>

                            <input
                                type="number"
                                min="1"
                                value={topLimit}
                                onChange={(event) => setTopLimit(event.target.value)}
                                placeholder="npr. 10"
                            />
                        </label>
                    </div>
                </div>

                {shouldShowChart && (
                    <ChartsDisplay
                        xValues={chartResult.xValues}
                        yValues={chartResult.yValues}
                        selectedY={chartTitle}
                        rawRows={chartResult.rawRows}
                        aggregation={aggregation}
                    />
                )}
            </>
        );
    };

    if (error) {
        return <div>Error: {error}</div>;
    }

    return (
        <DynamicModal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            title="Dijagram"
            customWidth={shouldShowChart ? "980px" : "760px"}
            customHeight={shouldShowChart ? "82vh" : "680px"}
            customZIndex={99999}
            contentPadding="0"
            bodyOverflowY="auto"
            centered={false}
            mainContent={
                <div
                    className={
                        shouldShowChart
                            ? "chart-modal-content has-chart"
                            : "chart-modal-content"
                    }
                >
                    {loading ? (
                        <div className="chart-loading-state">
                            Učitavanje podataka...
                        </div>
                    ) : (
                        renderFeatureInfo()
                    )}
                </div>
            }
            footerContent={
                <div className="app-modal-actions">
                    <button
                        type="button"
                        className="app-btn app-btn-success"
                        disabled={!shouldShowChart}
                        onClick={() => {
                            window.dispatchEvent(
                                new CustomEvent("chart-export-png", {
                                    detail: {
                                        filename: "dijagram-legenda.png",
                                    },
                                })
                            );
                        }}
                    >
                        Izvezi PNG
                    </button>

                    <button
                        type="button"
                        onClick={onRequestClose}
                        className="app-btn app-btn-secondary"
                    >
                        Zatvori
                    </button>
                </div>
            }
        />
    );
};

export default ChartSidebar;